import React, { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusCircle, Pencil, Loader2 } from 'lucide-react';
import { createOrgSchema, updateOrgSchema } from '@tyto/shared';
import { useNavigate } from 'react-router';

// Define the organization type
interface Organization {
  id: string;
  name: string;
  slug: string;
  telegramChatId?: string;
  createdAt: string;
  updatedAt: string;
}

type OrgFormValues = z.infer<typeof createOrgSchema>;

const AdminOrgs: React.FC = () => {
  const navigate = useNavigate();

  // Fetch organizations
  const { data: organizations, isLoading, error } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await fetch('/api/org');
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      return response.json() as Promise<Organization[]>;
    },
  });

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Organizations</h1>
        <Button asChild>
          <Link to="/app/admin/orgs/create">
            Create Organization
          </Link>
        </Button>
      </div>


      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load organizations. Please try again.
        </div>
      ) : (
        <div className="space-y-4">
          {organizations && organizations.length > 0 ? (
            <div className="grid gap-4">
              {organizations.map((org) => (
                <Card key={org.id} onClick={() => navigate(`/app/admin/org/${org.id}/`)}>
                  <CardHeader>
                    <div className="flex justify-between">
                      <CardTitle>{org.name}</CardTitle>
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/app/admin/org/${org.id}/edit`}>
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Link>
                      </Button>
                    </div>
                    <CardDescription>Slug: {org.slug}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium">Telegram Chat ID:</span>
                        <p className="text-sm text-muted-foreground">{org.telegramChatId || "-"}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium">Created:</span>
                        <p className="text-sm text-muted-foreground">{new Date(org.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              No organizations found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { AdminOrgs };
